import { create } from "zustand";

import type { CreateFavouriteRequest } from "src/core/backend/clients/backendClient";
import { logService } from "src/core/services/logService";
import type { ClinicDoctorModel } from "src/features/clinics/models/clinicDoctorModel";
import { FavouriteModel } from "../models/favouriteModel";
import { favouriteService } from "./favouriteService";

interface FavouriteState {
  readonly loading: boolean;
  readonly favourites: FavouriteModel[];

  clear: () => void;

  favouriteList: () => Promise<void>;
  favouriteDelete: (id: number) => Promise<void>;
  favouritePost: (clinicDoctor: ClinicDoctorModel) => Promise<void>;
}

export const useFavouriteStore = create<FavouriteState>()((set) => ({
  loading: false,
  favourites: [],

  clear: () => {
    set({
      loading: false,
      favourites: [],
    });
  },

  favouriteList: async () => {
    set({ loading: true });

    const response = await favouriteService.favouriteList();
    const favourites = response.map((item) => {
      return new FavouriteModel({
        id: item.id ?? 0,
        userId: item.userId ?? 0,
        clinicId: item.clinicId ?? 0,
        doctorId: item.doctorId ?? 0,
      });
    });

    set({ favourites: favourites, loading: false });
  },

  favouriteDelete: async (id: number) => {
    set({ loading: true });

    await favouriteService
      .favouriteDelete(id)
      .then(() => {
        if (logService.globalLog) logService.info("favouriteDelete", id);
      })
      .catch((error) => {
        logService.error("activate", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },

  favouritePost: async (clinicDoctor: ClinicDoctorModel) => {
    set({ loading: true });

    const request: CreateFavouriteRequest = {
      ...clinicDoctor,
    } as CreateFavouriteRequest;

    await favouriteService
      .favouritePost(request)
      .then(() => {
        if (logService.globalLog) logService.info("favouritePost", request);
      })
      .catch((error) => {
        logService.error("favouritePost", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },
}));
